/**
 * Carrying task-set membership across a task mutation.
 *
 * The timeline's tasks arrive on the REST scheduling payload (POST /scheduling),
 * which is the only place taskSetId/taskSetSequence exist. The task mutations do
 * not select them, and onTaskUpdated
 * (projects-scheduling/projects_scheduling_mutation_success.js) rebuilds the task
 * field by field from the mutation response before splicing it into data.tasks.
 * So without this, every drag, resize or modal edit silently strips a bar of its
 * set membership: the chip disappears and the click stops opening the drawer,
 * until the next full page load.
 *
 * It lives in its own module rather than inside that file because that file
 * imports most of the canvas graph, which cannot be loaded in a jest unit test.
 */

import type {TaskSetInstanceHint} from '../../../../forecast-app/project-tab/projects/scoping-page/task-set/taskSetDeleteRenumber';

export interface TaskSetMembership {
	projectId?: string | null;
	taskSetId?: string | null;
	taskSetSequence?: number | null;
}

/**
 * The set membership a delete mutation needs for a timeline task. The timeline's tasks are not
 * Relay records, so after the delete the store cannot tell which set the task was in; this hint
 * lets it lower that set's count. Undefined when the task is in no set.
 */
export const taskSetInstancesFor = (task: TaskSetMembership & {id: string}): TaskSetInstanceHint[] | undefined =>
	task.taskSetId ? [{taskId: task.id, taskSetId: task.taskSetId, position: task.taskSetSequence ?? null}] : undefined;

/**
 * Copies set membership from the pre-mutation task onto its replacement, in place.
 *
 * Deliberately NOT carried when the task changed project. Task sets are
 * project-scoped (ProjectType.taskSets), so membership in the old project's set is
 * not known to hold in the new one; leaving the replacement without membership
 * keeps the pre-PIKA-748 behaviour of no chip until the next load, which is better
 * than showing a confidently wrong one.
 */
export const carryTaskSetMembership = <T extends TaskSetMembership>(
	updatedTask: T,
	previousTask?: TaskSetMembership | null
): T => {
	if (!previousTask || previousTask.projectId !== updatedTask.projectId) {
		return updatedTask;
	}

	updatedTask.taskSetId = previousTask.taskSetId;
	updatedTask.taskSetSequence = previousTask.taskSetSequence;

	return updatedTask;
};
